import { useState } from 'react'
import { Play, Save, CheckCircle, Clock, X, BookOpen, MessageSquareText, GitBranch, Layers, Link } from 'lucide-react'
import { useApp } from '../context/AppContext'
import { getStatusColor, formatDate, generateId } from '../utils/helpers'

const taskTypes = ['Listing', 'Ads', 'Visual', 'QA']
const execModes = ['单 Step 执行', '多 Step 顺序执行', '仅生成输入模板', '仅保存人工输出', 'API 执行', '原始 GPTs/Gem 链接执行']

export default function TaskExecutor() {
  const { agents, prompts, knowledgeBases, tasks, setTasks, modelConfigs, canExecute } = useApp()
  const workflowAgents = agents.filter(a => a.agentType === 'workflow' && (a.status === '主用' || a.status === '测试'))
  const singleAgents = agents.filter(a => a.agentType === 'single' && (a.status === '主用' || a.status === '测试'))

  const [selectedAgentId, setSelectedAgentId] = useState('')
  const [selectedStepId, setSelectedStepId] = useState('')
  const [execMode, setExecMode] = useState('单 Step 执行')
  const [taskName, setTaskName] = useState('')
  const [input, setInput] = useState('')
  const [aiOutput, setAiOutput] = useState('')
  const [humanEdit, setHumanEdit] = useState('')
  const [isExecuting, setIsExecuting] = useState(false)

  const selectedAgent = agents.find(a => a.id === selectedAgentId)
  const selectedStep = selectedAgent?.steps?.find(s => s.stepId === selectedStepId)
  const selectedPrompt = prompts.find(p => p.id === (selectedStep?.systemPromptId || selectedAgent?.promptId))
  const selectedKBs = knowledgeBases.filter(k => (selectedStep?.knowledgeIds || selectedAgent?.knowledgeIds || []).includes(k.id))

  const execute = () => {
    if (!canExecute()) return alert('无权限')
    if (!selectedAgentId) return alert('请选择Agent')
    if (selectedAgent?.agentType === 'workflow' && !selectedStepId) return alert('请选择Step')
    if (!input.trim()) return alert('请输入内容')
    setIsExecuting(true); setAiOutput('')
    setTimeout(() => {
      const prefix = selectedStep ? `【Step ${selectedStep.stepNumber}: ${selectedStep.stepName} 模拟输出】\n\n` : '【模拟输出】\n\n'
      setAiOutput(prefix + `根据您提供的输入，AI 已生成以下结果：\n\n输入材料: ${input.substring(0, 50)}...\n\n输出标准: ${selectedStep?.outputStandard || '标准输出'}\n\n[此处为模拟输出内容，接入真实 API 后将返回实际结果]`)
      setIsExecuting(false)
    }, 1500)
  }

  const saveTask = (status = '待审核') => {
    if (!taskName) return alert('请输入任务名称')
    const newTask = {
      id: generateId(), name: taskName, type: selectedAgent?.module || '通用',
      workflowId: selectedAgent?.agentType === 'workflow' ? selectedAgent.id : '',
      stepId: selectedStepId || '',
      agentId: selectedAgentId, promptVersion: selectedPrompt?.version || '-',
      knowledgeVersion: selectedKBs.map(k => k.version).join(', ') || '-',
      model: selectedStep?.defaultModel || selectedAgent?.defaultModel || '-',
      inputFiles: [], inputText: input,
      aiOutput, humanEdit: humanEdit || null,
      adopted: status === '已采纳', reviewStatus: status,
      owner: 'Leo', reviewer: '',
      createdAt: new Date().toISOString(), updatedAt: new Date().toISOString()
    }
    setTasks([newTask, ...tasks]); alert('已保存'); reset()
  }

  const reset = () => { setTaskName(''); setInput(''); setAiOutput(''); setHumanEdit(''); setSelectedAgentId(''); setSelectedStepId(''); }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-800">任务执行台</h2>
        <button onClick={reset} className="btn-secondary text-sm">重置</button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="card p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">任务名称</label><input className="input" value={taskName} onChange={e => setTaskName(e.target.value)} placeholder="输入任务名称..." /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">执行方式</label>
                <select className="select" value={execMode} onChange={e => setExecMode(e.target.value)}>
                  {execModes.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">选择 Agent / 工作流</label>
              <select className="select" value={selectedAgentId} onChange={e => { setSelectedAgentId(e.target.value); setSelectedStepId('') }}>
                <option value="">请选择...</option>
                <optgroup label="工作流 Agent">
                  {workflowAgents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.module})</option>)}
                </optgroup>
                <optgroup label="单 Agent">
                  {singleAgents.map(a => <option key={a.id} value={a.id}>{a.name} ({a.module} / {a.source})</option>)}
                </optgroup>
              </select>
            </div>

            {selectedAgent?.agentType === 'workflow' && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">选择 Step</label>
                <div className="flex gap-2 flex-wrap">
                  {selectedAgent.steps?.map(step => (
                    <button key={step.stepId} onClick={() => setSelectedStepId(step.stepId)}
                      className={`px-3 py-1.5 rounded-lg text-sm ${selectedStepId === step.stepId ? 'bg-primary-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                      {step.stepNumber}. {step.stepName}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {selectedAgent && (
              <div className="bg-slate-50 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <MessageSquareText size={14} className="text-primary-600" />
                  <span className="text-slate-600">Prompt: {selectedPrompt?.name || '无'} {selectedPrompt?.version || ''}</span>
                </div>
                <div className="flex items-center gap-2">
                  <BookOpen size={14} className="text-primary-600" />
                  <span className="text-slate-600">知识库: {selectedKBs.map(k => k.name).join(', ') || '无'}</span>
                </div>
                <div className="text-slate-500">模型: {selectedStep?.defaultModel || selectedAgent.defaultModel} / 备用: {selectedStep?.backupModel || selectedAgent.backupModel || '-'}</div>
                {selectedStep?.inputRequirements && <div className="text-slate-500">输入要求: {selectedStep.inputRequirements}</div>}
                {selectedStep?.outputStandard && <div className="text-slate-500">输出标准: {selectedStep.outputStandard}</div>}
                {selectedStep?.needsReview && <div className="text-amber-600 text-xs">⚠️ 此 Step 需要人工审核</div>}
                {selectedStep?.originalLink && <div className="flex items-center gap-1 text-xs text-blue-600"><Link size={12} /> 原始链接: {selectedStep.originalLink}</div>}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">输入内容</label>
              <textarea className="textarea h-40" value={input} onChange={e => setInput(e.target.value)} placeholder="输入产品信息、广告数据、图片要求或QA内容..." />
            </div>

            <button onClick={execute} disabled={isExecuting} className="btn-primary w-full flex items-center justify-center gap-2">
              <Play size={16} /> {isExecuting ? '执行中...' : '执行 (模拟)'}
            </button>
          </div>

          {aiOutput && (
            <div className="card p-4 space-y-3">
              <h3 className="font-semibold text-slate-800">AI 输出</h3>
              <div className="bg-slate-50 rounded-lg p-4 text-sm text-slate-700 whitespace-pre-wrap font-mono">{aiOutput}</div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">人工修改版</label>
                <textarea className="textarea h-32" value={humanEdit} onChange={e => setHumanEdit(e.target.value)} placeholder="在此输入人工修改后的内容..." />
              </div>
              <div className="flex gap-3">
                <button onClick={() => saveTask('待审核')} className="btn-secondary flex items-center gap-2"><Save size={14} /> 保存待审核</button>
                <button onClick={() => saveTask('已采纳')} className="btn-primary flex items-center gap-2"><CheckCircle size={14} /> 保存并采纳</button>
              </div>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="card p-4">
            <h3 className="font-semibold text-slate-800 mb-3">最近任务</h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {tasks.slice(0, 10).map(task => (
                <div key={task.id} className="p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm text-slate-800 truncate">{task.name}</span>
                    <span className={`badge ${getStatusColor(task.reviewStatus)} text-[10px]`}>{task.reviewStatus}</span>
                  </div>
                  <div className="text-xs text-slate-500 flex items-center gap-2">
                    {task.workflowId && <GitBranch size={10} />}
                    <Clock size={10} /> {formatDate(task.createdAt)}
                  </div>
                </div>
              ))}
              {tasks.length === 0 && <div className="text-sm text-slate-400 text-center py-4">暂无任务</div>}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
